import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Label, LocalOffer, PictureAsPdf } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

export interface BatesDocument {
  id: string;
  title: string;
  fileName: string;
  mimeType?: string;
  metadata?: Record<string, unknown>;
}

interface BatesNumberingDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  documents: BatesDocument[];
  onSuccess?: () => void;
}

/** Normalize metadata.bates (object or legacy string) for display. */
export function getBatesDisplay(metadata?: Record<string, unknown> | null): {
  label: string | null;
  stamped: boolean;
  pageCount?: number;
} {
  if (!metadata) return { label: null, stamped: false };
  const raw = metadata.bates ?? metadata.batesRange;
  if (!raw) return { label: null, stamped: false };
  if (typeof raw === 'string') return { label: raw, stamped: false };
  if (typeof raw === 'object' && raw !== null) {
    const b = raw as {
      label?: string;
      stamped?: boolean;
      pageCount?: number;
      start?: number;
      end?: number;
      prefix?: string;
    };
    const label =
      b.label ||
      (b.prefix && b.start != null
        ? b.start === b.end
          ? `${b.prefix}-${String(b.start).padStart(6, '0')}`
          : `${b.prefix}-${String(b.start).padStart(6, '0')}–${b.prefix}-${String(b.end).padStart(6, '0')}`
        : null);
    return {
      label: label || null,
      stamped: Boolean(b.stamped),
      pageCount: b.pageCount,
    };
  }
  return { label: null, stamped: false };
}

const isPdfDoc = (doc: BatesDocument) => {
  const mime = (doc.mimeType || '').toLowerCase();
  const name = doc.fileName || '';
  return mime === 'application/pdf' || /\.pdf$/i.test(name);
};

const BatesNumberingDialog: React.FC<BatesNumberingDialogProps> = ({
  open,
  onClose,
  caseId,
  documents,
  onSuccess,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [prefix, setPrefix] = useState('PROD');
  const [startNumber, setStartNumber] = useState(1);
  const [stampPdfs, setStampPdfs] = useState(true);
  const [position, setPosition] = useState('bottom-right');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setPrefix('PROD');
    setStartNumber(1);
    setStampPdfs(true);
    setPosition('bottom-right');
    setError('');
  }, [open]);

  const selectedDocs = useMemo(
    () => documents.filter((d) => selectedIds.includes(d.id)),
    [documents, selectedIds]
  );
  const selectedPdfCount = selectedDocs.filter(isPdfDoc).length;

  const toggleDoc = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === documents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(documents.map((d) => d.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one document.');
      return;
    }
    if (!prefix.trim()) {
      setError('Prefix is required.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/cases/${caseId}/bates/assign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentIds: selectedIds,
          prefix: prefix.trim(),
          startNumber: startNumber > 0 ? startNumber : 1,
          stampPdfs,
          position: stampPdfs ? position : undefined,
        }),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to assign Bates numbers.'));
      }
      const json = await res.json();
      const stampedCount = (json.data?.assigned || []).filter((a: any) => a.bates?.stamped).length;
      toast.success(
        stampedCount > 0
          ? `Bates assigned — ${stampedCount} PDF(s) stamped on-page`
          : `Bates numbers assigned to ${selectedIds.length} document(s)`
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = toUserErrorMessage(err, 'Unable to assign Bates numbers.');
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const previewStart = `${prefix.trim() || 'PROD'}-${String(startNumber).padStart(6, '0')}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOffer color="primary" />
        Bates stamp
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Assign sequential Bates numbers to selected documents. When stamp is on, each PDF page
            is burned with its label and saved as a new document version.
          </Typography>

          {documents.length === 0 ? (
            <Alert severity="info">No documents available in this case folder.</Alert>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedIds.length === documents.length && documents.length > 0}
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < documents.length
                    }
                    onChange={toggleAll}
                  />
                }
                label={`Select all (${documents.length})`}
              />
              <List
                dense
                sx={{
                  maxHeight: 260,
                  overflow: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {documents.map((doc) => {
                  const existing = getBatesDisplay(doc.metadata);
                  const pdf = isPdfDoc(doc);
                  return (
                    <ListItem key={doc.id} disablePadding sx={{ alignItems: 'flex-start' }}>
                      <FormControlLabel
                        sx={{ width: '100%', mx: 1, alignItems: 'flex-start', py: 0.5 }}
                        control={
                          <Checkbox
                            checked={selectedIds.includes(doc.id)}
                            onChange={() => toggleDoc(doc.id)}
                            sx={{ pt: 0.5 }}
                          />
                        }
                        label={
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography variant="body2" component="span">
                                  {doc.title || doc.fileName}
                                </Typography>
                                {pdf && (
                                  <Chip
                                    size="small"
                                    icon={<PictureAsPdf />}
                                    label="PDF"
                                    variant="outlined"
                                    sx={{ height: 22 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              existing.label ? (
                                <Box
                                  component="span"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}
                                >
                                  <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{ fontFamily: 'monospace' }}
                                  >
                                    {existing.label}
                                  </Typography>
                                  {existing.stamped && (
                                    <Chip size="small" label="Stamped" color="success" sx={{ height: 20 }} />
                                  )}
                                </Box>
                              ) : (
                                doc.fileName
                              )
                            }
                          />
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="PROD"
              inputProps={{ maxLength: 20 }}
              helperText="Letters, numbers, _ or -"
            />
            <TextField
              fullWidth
              label="Start number"
              type="number"
              value={startNumber}
              onChange={(e) => setStartNumber(parseInt(e.target.value, 10) || 1)}
              inputProps={{ min: 1 }}
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={stampPdfs}
                onChange={(e) => setStampPdfs(e.target.checked)}
              />
            }
            label="Burn Bates onto PDF pages (creates new version)"
          />

          {stampPdfs && (
            <FormControl fullWidth size="small">
              <InputLabel>Stamp position</InputLabel>
              <Select
                label="Stamp position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              >
                <MenuItem value="bottom-right">Bottom right</MenuItem>
                <MenuItem value="bottom-left">Bottom left</MenuItem>
                <MenuItem value="top-right">Top right</MenuItem>
                <MenuItem value="top-left">Top left</MenuItem>
              </Select>
            </FormControl>
          )}

          {selectedIds.length > 0 && prefix.trim() && (
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Preview
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                First label: {previewStart}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {stampPdfs
                  ? `${selectedPdfCount} PDF(s) will be stamped page-by-page (multi-page PDFs consume sequential numbers). Non-PDFs get metadata only.`
                  : `${selectedIds.length} document(s) — metadata only (one Bates each).`}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Label />}
          onClick={handleSubmit}
          disabled={submitting || documents.length === 0 || selectedIds.length === 0}
        >
          {submitting
            ? stampPdfs
              ? 'Stamping…'
              : 'Assigning…'
            : stampPdfs
              ? 'Assign & stamp PDFs'
              : 'Assign Bates numbers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatesNumberingDialog;
